import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkles } from "lucide-react";
import { AssistantModal } from "./AssistantModal";

const NAV = [
  { key: "sports", label: "Sports", to: "/category/sports" },
  { key: "entertainment", label: "Entertainment", to: "/category/entertainment" },
  { key: "culture", label: "Culture", to: "/category/culture" },
  { key: "featured", label: "Featured Stories", to: "/category/featured" },
  { key: "about", label: "About Phusic", to: "/about" },
];

export const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navigation"
      className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-home-link"
          className="font-logo text-2xl text-white leading-none pt-1"
        >
          Phusic
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.key}
                to={item.to}
                data-testid={`nav-${item.key}-link`}
                className="group relative font-body text-sm uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors duration-300"
              >
                {item.label}
                <span
                  className={`absolute -bottom-1.5 left-0 h-[2px] bg-crimson transition-all duration-300 ${
                    active ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            );
          })}
          <button
            data-testid="nav-request-coverage"
            onClick={() => setAssistantOpen(true)}
            className="group relative inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors duration-300"
          >
            <Sparkles size={14} />
            Ask Phusic
            <span className="absolute -bottom-1.5 left-0 h-[2px] w-0 bg-crimson transition-all duration-300 group-hover:w-full" />
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV.map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  data-testid={`mobile-nav-${item.key}-link`}
                  className="py-3 font-body text-base uppercase tracking-[0.15em] text-white/80 hover:text-crimson transition-colors border-b border-white/5"
                >
                  {item.label}
                </Link>
              ))}
              <button
                data-testid="mobile-nav-request-coverage"
                onClick={() => { setOpen(false); setAssistantOpen(true); }}
                className="py-3 flex items-center gap-2 font-body text-base uppercase tracking-[0.15em] text-white/80 hover:text-crimson transition-colors"
              >
                <Sparkles size={15} />
                Ask Phusic
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      <AssistantModal open={assistantOpen} onClose={() => setAssistantOpen(false)} />
    </header>
  );
};
